import { useCallback, useEffect, useState } from 'react';
import { Shield, Trash2, Smartphone, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useAppSettings } from '../lib/app-settings-context';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

type FactorRow = {
  id: string;
  factor_type?: string;
  friendly_name?: string | null;
  status?: string;
};

export function AccountSecurity() {
  const { mfaEnabled } = useAppSettings();
  const [factors, setFactors] = useState<FactorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totpStep, setTotpStep] = useState<
    'idle' | { factorId: string; qrDataUrl: string; secret: string }
  >('idle');
  const [totpCode, setTotpCode] = useState('');
  const [busy, setBusy] = useState(false);

  const loadFactors = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      setFactors(data?.all ?? []);
    } catch (e) {
      console.error(e);
      toast.error('Could not load security factors');
      setFactors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!mfaEnabled) return;
    void loadFactors();
  }, [mfaEnabled, loadFactors]);

  const startTotpEnroll = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator app',
      });
      if (error || !data?.totp) throw error || new Error('TOTP enroll failed');
      const qrDataUrl = data.totp.qr_code.startsWith('data:')
        ? data.totp.qr_code
        : `data:image/svg+xml;utf-8,${encodeURIComponent(data.totp.qr_code)}`;
      setTotpStep({ factorId: data.id, qrDataUrl, secret: data.totp.secret });
      setTotpCode('');
      toast.message('Scan the QR code, then enter a code to confirm');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start authenticator setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async () => {
    if (totpStep === 'idle') return;
    setBusy(true);
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: totpStep.factorId,
      });
      if (chErr || !ch) throw chErr || new Error('Challenge failed');
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: totpStep.factorId,
        challengeId: ch.id,
        code: totpCode.replace(/\s/g, ''),
      });
      if (vErr) throw vErr;
      toast.success('Authenticator app linked');
      setTotpStep('idle');
      setTotpCode('');
      await loadFactors();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const cancelTotpSetup = async () => {
    if (totpStep === 'idle') return;
    const id = totpStep.factorId;
    setTotpStep('idle');
    setTotpCode('');
    try {
      await supabase.auth.mfa.unenroll({ factorId: id });
    } catch {
      /* factor may already be gone */
    }
    await loadFactors();
  };

  const addPasskey = async () => {
    const webauthn = (
      supabase.auth.mfa as {
        webauthn?: {
          register: (args: {
            friendlyName: string;
            webauthn?: { rpId?: string; rpOrigins?: string[]; signal?: AbortSignal };
          }) => Promise<{ error: Error | null }>;
        };
      }
    ).webauthn;
    if (!webauthn?.register) {
      toast.error('Passkeys require a current Supabase client with MFA WebAuthn enabled.');
      return;
    }
    setBusy(true);
    try {
      const { error } = await webauthn.register({
        friendlyName: `Passkey · ${new Date().toLocaleDateString()}`,
        webauthn: {},
      });
      if (error) throw error;
      toast.success('Passkey added');
      await loadFactors();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add passkey');
    } finally {
      setBusy(false);
    }
  };

  const removeFactor = async (factorId: string) => {
    if (!confirm('Remove this sign-in method? You can add it again later.')) return;
    setBusy(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      toast.success('Factor removed');
      await loadFactors();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not remove factor');
    } finally {
      setBusy(false);
    }
  };

  const verified = factors.filter((f) => f.status === 'verified');
  const unverified = factors.filter((f) => f.status !== 'verified');

  if (!mfaEnabled) {
    return null;
  }

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          <h2 className="text-lg font-medium">Account security</h2>
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Add an authenticator app or a passkey for two-factor sign-in. Enable MFA and WebAuthn in your Supabase
          project Auth settings if these options fail.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <>
            {verified.length > 0 && (
              <ul className="space-y-2">
                {verified.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/80"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                        {f.friendly_name || f.factor_type || 'Factor'}
                      </p>
                      <p className="text-xs text-neutral-500 capitalize">{f.factor_type?.replace('_', ' ')}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-shrink-0 text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-950"
                      onClick={() => removeFactor(f.id)}
                      disabled={busy}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {unverified.length > 0 && totpStep === 'idle' && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                You have an unfinished setup. Complete it below or remove the factor from the Supabase dashboard.
              </p>
            )}

            {totpStep === 'idle' ? (
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={startTotpEnroll} disabled={busy}>
                  <Smartphone className="w-4 h-4 mr-2" />
                  Add authenticator app
                </Button>
                <Button type="button" variant="outline" className="flex-1" onClick={addPasskey} disabled={busy}>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Add passkey
                </Button>
              </div>
            ) : (
              <div className="space-y-4 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <p className="text-sm text-neutral-700 dark:text-neutral-300">Scan with your authenticator app</p>
                <div className="flex justify-center bg-white dark:bg-neutral-900 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700">
                  <img src={totpStep.qrDataUrl} alt="TOTP QR" className="max-w-[200px] w-full h-auto" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-neutral-500">Manual entry secret</Label>
                  <code className="block text-xs font-mono break-all p-2 bg-neutral-100 dark:bg-neutral-900 rounded">
                    {totpStep.secret}
                  </code>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totp-confirm">6-digit code</Label>
                  <Input
                    id="totp-confirm"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\s/g, ''))}
                    placeholder="000000"
                    className="font-mono"
                    disabled={busy}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" className="flex-1" onClick={confirmTotp} disabled={busy || totpCode.length < 6}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={cancelTotpSetup} disabled={busy}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
